import { PageHeader, PageState, FilterBar, MetricCard, DataRows } from "@/components/ui/page";
import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { PermissionGate } from "@/features/auth/components/PermissionGate";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { ShoppingCart, DollarSign, TrendingUp, Receipt, AlertTriangle } from "lucide-react";
import {
	BarChart,
	Bar,
	PieChart,
	Pie,
	Sector,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	ResponsiveContainer,
	Cell,
} from "recharts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { reportsAPI, branchesAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatNumber } from "@/lib/utils";
import { Loading } from "@/components/ui/loader";
import { Price } from "@/components/ui/price";
import { useCurrency } from "@/hooks/useCurrency";
import { usePermission } from "@/hooks/usePermission";

interface DashboardStats {
	issuedInvoices: number;
	totalProfit: number;
	inventoryValue: number;
	averageSalesOperations: number;
	totalProducts: number;
	totalPurchases: number;
	cashAmount: number;
	networkAmount: number;
	creditAmount: number;
	totalWithTax: number;
	taxAmount: number;
	numberOfInvoices: number;
	averageInvoices: number;
	netSales: number;
}

interface Branch {
	id: string;
	name: string;
	status: string;
}

const date = new Date();
const today = date.toISOString().split("T")[0];
const twelveMonthsAgo = new Date(date.getFullYear() - 1, date.getMonth(), date.getDate()).toISOString().split("T")[0];

const DashboardPage: React.FC = () => {
	const { t } = useTranslation();
	const currency = useCurrency();
	// /branches needs branches.read; without it the filter is hidden and totals cover all branches.
	const canReadBranches = usePermission("branches.read");
	const [selectedBranch, setSelectedBranch] = useState<string>("all");
	const [dateFrom, setDateFrom] = useState<string>(twelveMonthsAgo);
	const [dateTo, setDateTo] = useState<string>(today);

	// Fetch dashboard data
	const {
		data: dashboardData,
		refetch: refetchDashboard,
		error: dashboardError,
		isLoading: isDashboardLoading,
	} = useQuery({
		queryKey: ["dashboard", selectedBranch, dateFrom, dateTo],
		queryFn: async () => {
			try {
				const result = await reportsAPI.getDashboardSummary({
					branchId: selectedBranch === "all" ? undefined : selectedBranch,
					startDate: dateFrom,
					endDate: dateTo,
				});
				return result;
			} catch (error) {
				throw error;
			}
		},
		enabled: !!dateFrom && !!dateTo,
		staleTime: 5 * 60 * 1000, // 5 minutes
		refetchOnWindowFocus: false,
	});

	const {
		data: branchesData,
		error: branchesError,
		refetch: refetchBranches,
	} = useQuery({
		queryKey: ["branches"],
		queryFn: () => branchesAPI.getAll(),
		enabled: canReadBranches,
	});

	const salesSummary = useMemo(() => dashboardData?.salesSummary || {}, [dashboardData?.salesSummary]);
	const revenueSummary = dashboardData?.revenueSummary || {};
	const productSummary = useMemo(() => dashboardData?.productSummary || {}, [dashboardData?.productSummary]);
	const collectedAmounts = dashboardData?.collectedAmounts || {};
	const topProducts = useMemo(() => dashboardData?.topProducts || [], [dashboardData?.topProducts]);
	const lowStockProducts = useMemo(() => dashboardData?.lowStockProducts || [], [dashboardData?.lowStockProducts]);

	// Process data for Sales vs Purchases chart
	const salesVsPurchasesData = useMemo(() => {
		return [
			{
				name: t("dashboard.charts.sales"),
				value: salesSummary?.totalRevenue || 0,
			},
			{
				name: t("dashboard.charts.purchases"),
				value: dashboardData?.purchasesSummary?.totalAmount || 0,
			},
		];
	}, [salesSummary?.totalRevenue, dashboardData?.purchasesSummary?.totalAmount, t]);

	// Process data for Top Products chart
	const topProductsChartData = useMemo(() => {
		return topProducts.slice(0, 5).map((product: any) => ({
			name: product.productName || product.name || t("common.noData"),
			revenue: parseFloat(product.revenue || product.totalRevenue || 0),
			quantity: Number(product.quantity || product.totalQuantity || 0),
		}));
	}, [topProducts, t]);

	// Process data for Inventory Status chart
	const inventoryStatusData = useMemo(() => {
		const totalProducts = productSummary?.totalProducts || 0;
		const lowStockCount = lowStockProducts.length || 0;
		const inStockCount = Math.max(0, totalProducts - lowStockCount);

		return [
			{
				name: t("dashboard.charts.inStock"),
				value: inStockCount,
			},
			{
				name: t("dashboard.charts.lowStock"),
				value: lowStockCount,
			},
		];
	}, [productSummary, lowStockProducts, t]);

	// Calculate stats from API data
	const stats: DashboardStats = {
		issuedInvoices: salesSummary?.totalRevenue || 0,
		totalProfit: dashboardData?.profitSummary?.profit || 0,
		inventoryValue: productSummary?.totalValue || 0,
		averageSalesOperations: salesSummary?.averageOrderValue || 0,
		totalProducts: productSummary?.totalProducts || 0,
		totalPurchases: dashboardData?.purchasesSummary?.totalAmount || 0,
		cashAmount: collectedAmounts?.cash || 0,
		networkAmount: collectedAmounts?.network || 0,
		creditAmount: collectedAmounts?.credit || 0,
		totalWithTax: collectedAmounts?.total || salesSummary?.totalRevenue || 0,
		taxAmount: revenueSummary?.totalTax || 0,
		numberOfInvoices: salesSummary?.totalSales || 0,
		averageInvoices: salesSummary?.averageOrderValue || 0,
		// Net = after discounts, excluding tax (netRevenue adds discounts back, so it is not net)
		netSales: (revenueSummary?.totalRevenue || 0) - (revenueSummary?.totalTax || 0),
	};

	const branches = (branchesData || []) as Branch[];

	const money = (value: number) => <Price value={value} currency={currency} />;
	const chartTooltip = {
		background: "hsl(var(--popover))",
		color: "hsl(var(--foreground))",
		border: "1px solid hsl(var(--border))",
		borderRadius: 12,
	};

	return (
		<div className="space-y-5 p-6 dashboard-workspace">
			<PageHeader
				title={t("navigation.dashboard")}
				description={t("design.dashboardDescription")}
				actions={
					<>
						<PermissionGate permission="sales.create">
							<Button asChild>
								<Link to="/sales/new">{t("sales.header.addButton")}</Link>
							</Button>
						</PermissionGate>
						<PermissionGate permission="products.create">
							<Button variant="outline" asChild>
								<Link to="/products/new">{t("products.actions.create")}</Link>
							</Button>
						</PermissionGate>
					</>
				}
			/>
			<FilterBar className="items-end">
				<div className="min-w-0 flex-1 basis-36 space-y-1.5">
					<Label htmlFor="dateFrom">{t("dashboard.filters.dateFrom")}</Label>
					<Input
						id="dateFrom"
						type="date"
						value={dateFrom}
						onChange={e => setDateFrom(e.target.value)}
						max={dateTo || undefined}
					/>
				</div>
				<div className="min-w-0 flex-1 basis-36 space-y-1.5">
					<Label htmlFor="dateTo">{t("dashboard.filters.dateTo")}</Label>
					<Input
						id="dateTo"
						type="date"
						value={dateTo}
						onChange={e => setDateTo(e.target.value)}
						min={dateFrom || undefined}
					/>
				</div>
				{canReadBranches && (
					<div className="min-w-0 flex-1 basis-40 space-y-1.5">
						<Label htmlFor="branchSelect">{t("dashboard.filters.branch")}</Label>
						<Select value={selectedBranch} onValueChange={setSelectedBranch}>
							<SelectTrigger id="branchSelect">
								<SelectValue placeholder={t("dashboard.filters.selectBranch")} />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">{t("dashboard.filters.allBranches")}</SelectItem>
								{branches.map(branch => (
									<SelectItem key={branch.id} value={String(branch.id)}>
										{branch.name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				)}
			</FilterBar>
			{branchesError && (
				<div role="alert" className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
					<span>{t("referenceUI.branchesUnavailable")}</span>
					<Button variant="outline" size="sm" onClick={() => refetchBranches()}>
						{t("common.retry")}
					</Button>
				</div>
			)}
			{dashboardError ? (
				<Card>
					<PageState
						error
						title={t("common.errorLoading")}
						action={<Button onClick={() => refetchDashboard()}>{t("common.tryAgain")}</Button>}
					/>
				</Card>
			) : isDashboardLoading ? (
				<Card>
					<Loading />
				</Card>
			) : (
				<>
					<div
						className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4"
						data-testid="dashboard-metrics"
					>
						<MetricCard
							icon={DollarSign}
							tone="info"
							label={t("dashboard.stats.issuedInvoices")}
							value={money(stats.issuedInvoices)}
							hint={t("dashboard.stats.issuedInvoicesDescription")}
						/>
						<MetricCard
							icon={TrendingUp}
							tone={Number(stats.totalProfit) < 0 ? "danger" : "success"}
							label={t("dashboard.stats.totalProfit")}
							value={money(stats.totalProfit)}
							hint={t("dashboard.stats.totalProfitDescription")}
						/>
						<MetricCard
							icon={Receipt}
							tone="violet"
							label={t("dashboard.stats.averageSalesOperations")}
							value={money(stats.averageSalesOperations)}
							hint={t("dashboard.stats.averageSalesOperationsDescription")}
						/>
						<MetricCard
							icon={ShoppingCart}
							tone="orange"
							label={t("dashboard.stats.purchases")}
							value={money(stats.totalPurchases)}
							hint={t("dashboard.stats.purchasesDescription")}
						/>
					</div>
					{lowStockProducts.length > 0 && (
						<PermissionGate permission={["products.read", "inventory.read"]}>
							<section className="attention-strip" aria-label={t("dashboard.charts.lowStock")}>
								<div className="flex items-center gap-2 text-sm font-semibold">
									<AlertTriangle className="h-4 w-4 text-warning" aria-hidden />
									{t("dashboard.charts.lowStock")}
									<span className="text-muted-foreground">{lowStockProducts.length}</span>
								</div>
								<div className="flex flex-wrap gap-2">
									{lowStockProducts.map((product: any, index: number) => (
										<Link
											key={product.productId || product.id || index}
											className="attention-link"
											to={
												product.productId || product.id
													? `/products/${product.productId || product.id}`
													: `/products?q=${encodeURIComponent(product.productName || product.name || "")}`
											}
										>
											<span>{product.productName || product.name}</span>
											<span className="font-semibold tabular-nums">
												{product.quantity ?? product.stock ?? "—"}
											</span>
										</Link>
									))}
								</div>
							</section>
						</PermissionGate>
					)}
					<div className="grid items-start gap-4 xl:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)]">
						<Card>
							<CardHeader>
								<CardTitle>{t("dashboard.charts.salesVsPurchases")}</CardTitle>
							</CardHeader>
							<CardContent>
								{!salesVsPurchasesData.some(item => item.value !== 0) ? (
									<PageState title={t("common.noData")} />
								) : (
									<>
										<div dir="ltr" role="img" aria-label={t("dashboard.charts.salesVsPurchases")}>
											<ResponsiveContainer width="100%" height={220}>
												<BarChart
													data={salesVsPurchasesData}
													margin={{ top: 10, right: 12, left: 0, bottom: 0 }}
												>
													<CartesianGrid
														vertical={false}
														stroke="hsl(var(--border))"
														strokeDasharray="3 3"
													/>
													<XAxis
														dataKey="name"
														tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
														tickLine={false}
														axisLine={false}
													/>
													<YAxis
														width={60}
														tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
														tickLine={false}
														axisLine={false}
														tickFormatter={value => formatNumber(value)}
													/>
													<Tooltip
														contentStyle={chartTooltip}
														cursor={{ fill: "hsl(var(--muted))" }}
														formatter={(value: number) => [formatNumber(value), currency]}
													/>
													<Bar
														dataKey="value"
														radius={[6, 6, 0, 0]}
														maxBarSize={76}
														isAnimationActive={false}
													>
														{salesVsPurchasesData.map((item, index) => (
															<Cell
																key={item.name}
																fill={
																	index === 0
																		? "hsl(var(--info))"
																		: "hsl(var(--orange))"
																}
															/>
														))}
													</Bar>
												</BarChart>
											</ResponsiveContainer>
										</div>
										<DataRows
											rows={salesVsPurchasesData.map(item => ({
												label: item.name,
												value: money(item.value),
											}))}
										/>
									</>
								)}
							</CardContent>
						</Card>
						<Card>
							<CardHeader>
								<CardTitle>{t("dashboard.charts.topProducts")}</CardTitle>
							</CardHeader>
							<CardContent>
								{topProductsChartData.length === 0 ? (
									<PageState title={t("common.noData")} />
								) : (
									<ol className="space-y-4">
										{topProductsChartData.map((product: any, index: number) => {
											const maximum = Math.max(
												...topProductsChartData.map(
													(item: { revenue: number }) => item.revenue,
												),
												0,
											);
											return (
												<li key={index} className="space-y-2">
													<div className="flex items-start gap-3">
														<span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-muted text-xs text-muted-foreground">
															{index + 1}
														</span>
														<div className="min-w-0 flex-1">
															<p className="break-words text-sm font-medium">
																{product.name}
															</p>
															<p className="mt-0.5 text-xs text-muted-foreground">
																{t("common.quantity")}: {formatNumber(product.quantity)}
															</p>
														</div>
														<div className="shrink-0 text-sm font-semibold tabular-nums">
															{money(product.revenue)}
														</div>
													</div>
													<div
														className="h-1 overflow-hidden rounded-full bg-muted"
														data-revenue-bar={product.revenue}
													>
														<div
															className="h-full rounded-full bg-primary"
															style={{
																width: `${maximum > 0 ? Math.max(0, (product.revenue / maximum) * 100) : 0}%`,
															}}
														/>
													</div>
												</li>
											);
										})}
									</ol>
								)}
							</CardContent>
						</Card>
					</div>
					<div className="grid items-start gap-4 lg:grid-cols-3">
						<Card>
							<CardHeader>
								<CardTitle>{t("dashboard.collectedAmounts.title")}</CardTitle>
							</CardHeader>
							<CardContent>
								<DataRows
									rows={[
										{ label: t("dashboard.collectedAmounts.cash"), value: money(stats.cashAmount) },
										{
											label: t("dashboard.collectedAmounts.network"),
											value: money(stats.networkAmount),
										},
										{
											label: t("dashboard.collectedAmounts.credit"),
											value: money(stats.creditAmount),
										},
										{
											label: t("dashboard.collectedAmounts.totalWithTax"),
											value: money(stats.totalWithTax),
											total: true,
										},
									]}
								/>
							</CardContent>
						</Card>
						<Card>
							<CardHeader>
								<CardTitle>{t("dashboard.sales.title")}</CardTitle>
							</CardHeader>
							<CardContent>
								<DataRows
									rows={[
										{ label: t("dashboard.sales.tax"), value: money(stats.taxAmount) },
										{
											label: t("dashboard.sales.numberOfInvoices"),
											value: formatNumber(stats.numberOfInvoices),
										},
										{
											label: t("dashboard.sales.averageInvoices"),
											value: money(stats.averageInvoices),
										},
										{
											label: t("dashboard.sales.netSales"),
											value: money(stats.netSales),
											total: true,
										},
									]}
								/>
							</CardContent>
						</Card>
						<Card>
							<CardHeader>
								<CardTitle>{t("dashboard.charts.inventoryStatus")}</CardTitle>
							</CardHeader>
							<CardContent>
								<DataRows
									rows={[
										{
											label: t("dashboard.stats.totalProducts"),
											value: formatNumber(stats.totalProducts),
										},
										{
											label: t("dashboard.stats.inventoryValue"),
											value: money(stats.inventoryValue),
										},
									]}
								/>
								{stats.totalProducts === 0 ? (
									<PageState title={t("common.noData")} />
								) : (
									<>
										<div dir="ltr" role="img" aria-label={t("dashboard.charts.inventoryStatus")}>
											<ResponsiveContainer width="100%" height={170}>
												<PieChart>
													<Pie
														data={inventoryStatusData}
														dataKey="value"
														cx="50%"
														cy="50%"
														outerRadius={70}
														innerRadius={48}
														paddingAngle={2}
														isAnimationActive={false}
														activeIndex={inventoryStatusData.map((_, index) => index)}
														activeShape={(props: any) => (
															<Sector
																{...props}
																role="img"
																aria-label={`${props.name}: ${props.value}`}
															/>
														)}
													>
														{inventoryStatusData.map((item, index) => (
															<Cell
																key={item.name}
																fill={
																	index === 0
																		? "hsl(var(--success))"
																		: "hsl(var(--warning))"
																}
																stroke="hsl(var(--card))"
															/>
														))}
													</Pie>
													<Tooltip contentStyle={chartTooltip} />
												</PieChart>
											</ResponsiveContainer>
										</div>
										<DataRows
											rows={inventoryStatusData.map(item => ({
												label: item.name,
												value: formatNumber(item.value),
											}))}
										/>
									</>
								)}
							</CardContent>
						</Card>
					</div>
				</>
			)}
		</div>
	);
};
export default DashboardPage;
